import React, {useState, useEffect} from "react";
import {useMutation, useQuery} from "react-query";
import { ISellerJson} from "./seller.interface";
import {apiClient, IGetAllParams, Typography} from "@leap4/frontend";
import {sellersBaseUrl} from "../../../config/generals";

import {ISite} from "../sites/site.interface";
import {fetchSite} from "../sites/siteManager";

import {SellerContext} from "./sellerContext";
import SellerTabs from "./sellerTabs";


const fetchCurrentSeller = async (): Promise<ISellerJson> => {
    const params: IGetAllParams = {
        fields: {
            _id: 1,
            created: 1,
            contact_email: 1,
            contact_address: 1,
            version: 1,
            identifiers: 1,
            sellers: 1,
            isChanged: 1,
            isValidate: 1,
            updated: 1,
        },
        pageSize: 1,
        page: 1,
        sort: { created: -1 },
    };

    const currentSellerRes = await apiClient.get(sellersBaseUrl, { params: params });
    const currentSeller = currentSellerRes.data.results[0] as ISellerJson;

    return currentSeller;
};

const fetchLastChangedSeller = async (currentSellerId: string): Promise<ISellerJson | null> => {
    const params: IGetAllParams = {
        fields: {
            _id: 1,
            created: 1,
            contact_email: 1,
            contact_address: 1,
            version: 1,
            identifiers: 1,
            sellers: 1,
            isChanged: 1,
            isValidate: 1,
            updated: 1,
        },
        pageSize: 1,
        page: 1,
        sort: { created: -1 },
        filter: {
            _id: { $ne: currentSellerId },
            isChanged: true,
        },
    };

    const lastChangeSellerRes = await apiClient.get(sellersBaseUrl, { params: params });
    const lastChangeSeller = lastChangeSellerRes.data.results[0] as ISellerJson;

    return lastChangeSeller || null;
};

const fetchLastTwoSellers = async (): Promise<{ currentSeller: ISellerJson; lastChangeSeller: ISellerJson | null }> => {
    const currentSeller = await fetchCurrentSeller();
    const lastChangeSeller = await fetchLastChangedSeller(currentSeller._id);
    return {
        currentSeller,
        lastChangeSeller,
    };
};

const SellersManager: React.FC = () => {
    // Local state for sellers - children can update these via context
    const [currentSeller, setCurrentSeller] = useState<ISellerJson | undefined>(undefined);
    const [chosenSeller, setChosenSeller] = useState<ISellerJson | null>(null);

    // Fetch sellers with staleTime for smart caching
    const { data: sellerData, isLoading: isLoadingLastTwoSellers } = useQuery(
        'sellers',
        fetchLastTwoSellers,
        {
            refetchOnWindowFocus: false,
            staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes
            refetchOnReconnect: false,
        }
    );

    // Sync local state when query data arrives (only when data changes)
    useEffect(() => {
        if (sellerData) {
            setCurrentSeller(sellerData.currentSeller);
            setChosenSeller(sellerData.lastChangeSeller);
        }
    }, [sellerData]);

    const { data: siteData, isLoading: isLodingSiteData } = useQuery<ISite>('site', fetchSite, {
        refetchOnWindowFocus: false,
        staleTime: 2 * 60 * 1000, // Data is fresh for 2 minutes
        refetchOnReconnect: false,
    });

    // Mutation to rescrape the last seller
    const reScrapeSeller = async (sellerId: string): Promise<ISellerJson> => {
        const res = await apiClient.patch(`${sellersBaseUrl}/reScrape/${sellerId}`);
        return res.data as ISellerJson;
    };

    const reScrapeMutation = useMutation(reScrapeSeller, {
        onSuccess: (updatedSeller) => {
            setCurrentSeller(updatedSeller);
        },
    });

    const handleReScrapeLastSeller = () => {
        if (currentSeller?._id) {
            reScrapeMutation.mutate(currentSeller._id);
        }
    };

    const openSellerFile = () => {
        if (siteData?.domain) {
            // Ensure the protocol is included in the URL
            let url = "";
            if (siteData.domain.includes('http') || siteData.domain.includes('https')) {
                url = `${siteData.domain}/sellers.json`;
            } else {
                url = `https://${siteData.domain}/sellers.json`;
            }
            window.open(url, '_blank');
        }
    };

    if (isLoadingLastTwoSellers || isLodingSiteData || !currentSeller) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <SellerContext.Provider value={{ 
            chosenSeller, 
            setChosenSeller, 
            currentSeller, 
            setCurrentSeller, 
            site: siteData
        }}>
            <SellerTabs handleReScrapeLastSeller={handleReScrapeLastSeller} openSellerFile={openSellerFile} reScrapeMutation={reScrapeMutation} />
        </SellerContext.Provider>
    );
};

export default SellersManager;

